/**
 * MODULE: assistantConnectorOutbound.ts
 * PURPOSE: Apply connector selection to outbound assistant messages and Langflow flow id.
 */

import type { AssistantConnectorItem, AssistantConnectorSelection } from './assistantConnectorTypes';

export function isConnectorSelected(
  item: AssistantConnectorItem,
  selection: AssistantConnectorSelection
): boolean {
  if (item.kind === 'web') return selection.web;
  if (item.kind === 'langflow_flow' && item.flowId) {
    return selection.flowIds.includes(item.flowId);
  }
  if (item.kind === 'mcp_tool' && item.mcpTool) {
    return selection.mcpTools.includes(item.mcpTool);
  }
  return false;
}

export function toggleConnectorInSelection(
  item: AssistantConnectorItem,
  selection: AssistantConnectorSelection
): AssistantConnectorSelection {
  if (item.kind === 'web') {
    return { ...selection, web: !selection.web };
  }
  if (item.kind === 'langflow_flow' && item.flowId) {
    const on = selection.flowIds.includes(item.flowId);
    return {
      ...selection,
      flowIds: on
        ? selection.flowIds.filter((id) => id !== item.flowId)
        : [...selection.flowIds, item.flowId],
    };
  }
  if (item.kind === 'mcp_tool' && item.mcpTool) {
    const on = selection.mcpTools.includes(item.mcpTool);
    return {
      ...selection,
      mcpTools: on
        ? selection.mcpTools.filter((name) => name !== item.mcpTool)
        : [...selection.mcpTools, item.mcpTool],
    };
  }
  return selection;
}

/** Prefer a single user-selected Langflow flow when running direct Langflow mode. */
export function resolvePrimaryLangflowFlowId(
  selection: AssistantConnectorSelection,
  fallbackFlowId: string
): string {
  if (selection.flowIds.length === 1) return selection.flowIds[0]!;
  if (selection.flowIds.length > 1) return selection.flowIds[0]!;
  return fallbackFlowId;
}

/** Hidden routing hint prepended to API messages (not shown in user bubble). */
export function buildAssistantConnectorOutboundMessage(
  displayMessage: string,
  selection: AssistantConnectorSelection,
  catalog: AssistantConnectorItem[]
): string {
  const trimmed = displayMessage.trim();
  if (!trimmed) return trimmed;

  const flowLabels = catalog
    .filter((c) => c.kind === 'langflow_flow' && c.flowId && selection.flowIds.includes(c.flowId))
    .map((c) => c.label);
  const mcpLabels = catalog
    .filter((c) => c.kind === 'mcp_tool' && c.mcpTool && selection.mcpTools.includes(c.mcpTool))
    .map((c) => c.label);

  const hasExtra =
    !selection.web || flowLabels.length > 0 || mcpLabels.length > 0;
  if (!hasExtra) return trimmed;

  const lines = ['[CONNECTORS]'];
  lines.push(`web=${selection.web ? 'true' : 'false'}`);
  if (flowLabels.length) lines.push(`langflow_flows=${flowLabels.join(', ')}`);
  if (selection.flowIds.length) lines.push(`langflow_flow_ids=${selection.flowIds.join(',')}`);
  if (mcpLabels.length) lines.push(`mcp_tools=${mcpLabels.join(', ')}`);
  lines.push('[/CONNECTORS]');
  return `${lines.join('\n')}\n${trimmed}`;
}

export function countActiveConnectors(selection: AssistantConnectorSelection): number {
  return (
    (selection.web ? 1 : 0) + selection.flowIds.length + selection.mcpTools.length
  );
}
